import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { journalService, userService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import HIPAAWarningModal from '../components/HIPAAWarningModal';
import { Badge, Button, Card } from '../components/ui';

const ClientJournalView = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [entries, setEntries] = useState([]);
  const [hasJournalAccess, setHasJournalAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHIPAAWarning, setShowHIPAAWarning] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [entryContent, setEntryContent] = useState({});

  useEffect(() => {
    const loadClientData = async () => {
      try {
        const [clientResponse, entriesResponse] = await Promise.all([
          userService.getProfile(clientId),
          journalService.getAllEntries(clientId)
        ]);

        setClient(clientResponse.data.data.user);
        setEntries(entriesResponse.data.data.entries || []);
        setHasJournalAccess(entriesResponse.data.data.hasJournalAccess || false);
        
        // Show HIPAA warning on first load
        if (!hasAcknowledged && entriesResponse.data.data.entries.length > 0) {
          setShowHIPAAWarning(true);
        }
      } catch (error) {
        console.error('Failed to load client journal:', error);
        alert('Failed to load journal data');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    loadClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleAcceptHIPAA = () => {
    setShowHIPAAWarning(false);
    setHasAcknowledged(true);
  };

  const handleViewEntry = async (entryId, day) => {
    if (!hasAcknowledged) {
      setShowHIPAAWarning(true);
      return;
    }

    if (expandedEntry === entryId) {
      setExpandedEntry(null);
      return;
    }

    try {
      const response = await journalService.getEntry(clientId, day);
      setEntryContent({
        ...entryContent,
        [entryId]: response.data.data.entry.content
      });
      setExpandedEntry(entryId);
    } catch (error) {
      console.error('Failed to load entry content:', error);
      if (error.response?.status === 403) {
        alert('This journal entry is private and the client has not granted access.');
      } else {
        alert('Failed to load journal entry');
      }
    }
  };

  const getMoodColor = (mood) => {
    if (mood <= 3) return 'text-red-600';
    if (mood <= 6) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getCravingColor = (craving) => {
    if (craving >= 8) return 'text-red-600';
    if (craving >= 5) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return <LoadingSpinner message="Loading journal..." />;
  }

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <HIPAAWarningModal
          isOpen={showHIPAAWarning}
          onClose={() => {
            setShowHIPAAWarning(false);
            navigate(-1);
          }}
          onAccept={handleAcceptHIPAA}
          userName={`${client?.firstName} ${client?.lastName}`}
          userRole="clinician"
        />

        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            ← Back
          </Button>
          <Badge variant="primary" size="sm">Client Journal</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            {client?.firstName} {client?.lastName}'s Journal
          </h2>
          <p className="mt-2 text-neutral-600">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'} available
          </p>
        </div>

        {/* Access Status */}
        <Card className={`mb-6 border-l-4 ${hasJournalAccess ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100">
              {hasJournalAccess ? '✓' : '🔒'}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-display font-semibold text-neutral-800">
                Journal Access Status
              </h3>
              <p className="mt-1 text-sm text-neutral-600">
                {hasJournalAccess ? (
                  <>
                    This client has <strong>granted permission</strong> for you to view their private journal entries. 
                    All entries, including those marked as private, are visible to you. This access can be revoked by 
                    the client at any time.
                  </>
                ) : (
                  <>
                    This client has <strong>not granted permission</strong> for you to view their private journal entries. 
                    You can only view entries that are marked as shared. Private entries will be hidden and display 
                    an access denied message if attempted to view.
                  </>
                )}
              </p>
              <Badge variant={hasJournalAccess ? 'success' : 'warning'} size="sm" className="mt-2">
                {hasJournalAccess ? 'Full Access Granted' : 'Limited Access'}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Journal Entries */}
        <Card>
          <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">
            Journal Entries
          </h3>

          {entries.length === 0 ? (
            <div className="py-8 text-center text-neutral-500">
              <p>No journal entries recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.slice().reverse().map((entry) => (
                <div
                  key={entry._id}
                  className="border border-neutral-200 rounded-lg p-4 hover:border-neutral-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-medium text-neutral-800">Day {entry.day}</h4>
                        <Badge 
                          variant={entry.isPrivate ? 'warning' : 'neutral'} 
                          size="sm"
                        >
                          {entry.isPrivate ? '🔒 Private' : '👁️ Shared'}
                        </Badge>
                        {entry.isFullyComplete && (
                          <Badge variant="success" size="sm">Complete</Badge>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-neutral-600">
                        {new Date(entry.date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>

                      {/* Tracking Metrics */}
                      <div className="mt-3 flex gap-4 text-sm">
                        <div>
                          <span className="text-neutral-500">Mood: </span>
                          <span className={`font-medium ${getMoodColor(entry.tracking?.moodScale)}`}>
                            {entry.tracking?.moodScale}/10
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500">Challenge: </span>
                          <span className={`font-medium ${getCravingColor(entry.tracking?.cravingScale)}`}>
                            {entry.tracking?.cravingScale}/10
                          </span>
                        </div>
                        {entry.tracking?.sleepHours && (
                          <div>
                            <span className="text-neutral-500">Sleep: </span>
                            <span className="font-medium text-neutral-700">
                              {entry.tracking.sleepHours}h
                            </span>
                          </div>
                        )}
                        {entry.tracking?.exerciseMinutes > 0 && (
                          <div>
                            <span className="text-neutral-500">Exercise: </span>
                            <span className="font-medium text-neutral-700">
                              {entry.tracking.exerciseMinutes}min
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Completion Status */}
                      <div className="mt-2 flex gap-2">
                        {entry.completed?.mind && (
                          <Badge variant="phase.sustain" size="sm">✓ Mind</Badge>
                        )}
                        {entry.completed?.move && (
                          <Badge variant="phase.rebuild" size="sm">✓ Move</Badge>
                        )}
                        {entry.completed?.journal && (
                          <Badge variant="phase.understand" size="sm">✓ Journal</Badge>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="ml-4">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleViewEntry(entry._id, entry.day)}
                      >
                        {expandedEntry === entry._id ? 'Hide Content' : 'View Content'}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedEntry === entry._id && entryContent[entry._id] && (
                    <div className="mt-4 pt-4 border-t border-neutral-200">
                      <div className="space-y-3 text-sm bg-neutral-50 p-4 rounded-md">
                        <p className="font-medium text-neutral-700">Journal Content:</p>
                        <div className="text-neutral-600 whitespace-pre-wrap">
                          {JSON.stringify(entryContent[entry._id], null, 2)}
                        </div>
                        {entry.tracking?.gratitudeItems && entry.tracking.gratitudeItems.length > 0 && (
                          <div className="mt-3">
                            <p className="font-medium text-neutral-700 mb-1">Gratitude Items:</p>
                            <ul className="ml-4 list-disc text-neutral-600 space-y-1">
                              {entry.tracking.gratitudeItems.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </main>
    </>
  );
};

export default ClientJournalView;
